import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtErr } from "./api";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-brand-navy flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/assets/bms-logo.png" alt="BMS Health Care Services" className="h-20 w-auto mx-auto bg-white rounded-2xl p-2" />
          <h1 className="mt-6 font-display text-3xl text-white">Choose a New Password</h1>
        </div>
        <div className="rounded-2xl bg-white p-8 shadow-xl">
          {done ? (
            <div data-testid="reset-success" className="text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-brand-teal" strokeWidth={1.5} />
              <p className="mt-4 text-base text-slate-600">Your password has been reset successfully. You can now sign in with your new password.</p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/portal/login" data-testid="reset-admin-login" className="rounded-full bg-brand-teal px-5 py-3 text-sm font-bold text-white hover:bg-brand-navy transition-colors duration-300">Office Admin Sign In</Link>
                <Link to="/caregiver/login" data-testid="reset-caregiver-login" className="rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-brand-navy hover:bg-brand-mist transition-colors duration-300">Caregiver Sign In</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {!token && <p className="text-sm font-semibold text-red-700">This reset link is missing its token. Please use the link from your email.</p>}
              <div>
                <label htmlFor="reset-password-input" className="block text-sm font-bold text-brand-navy">New Password</label>
                <input
                  id="reset-password-input"
                  data-testid="reset-password-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
                />
              </div>
              <div>
                <label htmlFor="reset-confirm-input" className="block text-sm font-bold text-brand-navy">Confirm New Password</label>
                <input
                  id="reset-confirm-input"
                  data-testid="reset-confirm-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal"
                />
              </div>
              {error && <p data-testid="reset-error" className="text-sm font-semibold text-red-700" role="alert">{error}</p>}
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={loading || !token}
                className="w-full rounded-full bg-brand-teal px-6 py-3.5 text-base font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95 disabled:opacity-60"
              >
                {loading ? "Resetting…" : "Reset Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
